import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";
import formatDate from "./formatDate";

// Quarter-hour dropdowns matching AvailabilityProposalCard — native time
// inputs don't flex-shrink and overflow the docked panel. Event rows store
// times as decimal hours (19.5 = 7:30 PM), the same unit the options use.
const QHNUM_OPTIONS = (() => {
  const out = [];
  for (let q = 0; q < 24 * 4; q++) {
    out.push(q / 4);
  }
  return out;
})();

function fmtQhnum(qhnum) {
  if (qhnum === null || qhnum === undefined) return "";
  const hour = Math.floor(qhnum) % 24;
  const minute = Math.round((qhnum - Math.floor(qhnum)) * 60);
  const period = hour < 12 ? "AM" : "PM";
  let displayHour = hour > 12 ? hour - 12 : hour;
  if (displayHour === 0) displayHour = 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
}

export default function DayNoteProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const summary = proposal.summary || {};
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(summary.visible_to_employees !== false);
  const [description, setDescription] = useState(String(summary.description ?? ""));
  // Both times must be present — a one-sided proposal would seed NaN
  // into the pickers and post an invalid override on plain confirm.
  const hasProposedTimes =
    summary.start_time != null && summary.end_time != null;
  const [hasTimes, setHasTimes] = useState(hasProposedTimes);
  // Snap proposed times to the quarter-hour grid the selects offer.
  // Starts max out at 23.75; ends may be 24 (midnight) — the executor
  // accepts end_time <= 24, and clamping it would silently shorten the
  // note by 15 minutes on a plain confirm.
  const snapStart = (v) => Math.min(Math.max(Math.round(v * 4) / 4, 0), 23.75);
  const snapEnd = (v) => Math.min(Math.max(Math.round(v * 4) / 4, 0.25), 24);
  const [startTime, setStartTime] = useState(
    hasProposedTimes ? snapStart(summary.start_time) : 12.0
  );
  const [endTime, setEndTime] = useState(
    hasProposedTimes ? snapEnd(summary.end_time) : 13.0
  );

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  const timesInvalid = hasTimes && endTime <= startTime;

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action: action,
      };
      if (action === "confirm") {
        const overrides = {};
        if (visible !== (summary.visible_to_employees !== false)) {
          overrides.visible_to_employees = visible;
        }
        if (description.trim() !== String(summary.description ?? "").trim()) {
          overrides.description = description.trim();
        }
        const newStart = hasTimes ? startTime : null;
        const newEnd = hasTimes ? endTime : null;
        if (newStart !== (summary.start_time ?? null)) {
          overrides.start_time = newStart;
        }
        if (newEnd !== (summary.end_time ?? null)) {
          overrides.end_time = newEnd;
        }
        if (Object.keys(overrides).length > 0) {
          payload.overrides = overrides;
        }
      }
      const res = await postRightworkAPI(API_ops_assistant_confirm_action, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoading(false);
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Day note added</div>
          {summary.note && (
            <div className="text-xs opacity-80">{summary.note}</div>
          )}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Day note cancelled</span>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <EventNoteIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">Add Note to Day</span>
      </div>

      <div className="px-3 py-2 space-y-2">
        <div className="text-sm">
          <span className="text-gray-500">Date: </span>
          <span className="font-medium text-gray-700">{formatDate(summary.dates)}</span>
        </div>
        <div className="text-sm text-gray-700 border border-gray-200 bg-white rounded-md px-3 py-2">
          {summary.note}
        </div>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 resize-none placeholder:text-gray-400"
          rows={Math.min(Math.max(description.split("\n").length, 2), 6)}
          placeholder="Add detail (optional)..."
        />

        {/* Time window — quarter-hour selects on their own row so they
            shrink inside the docked panel (matches the availability
            card's pickers). */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Time</span>
          <button
            type="button"
            onClick={() => setHasTimes(!hasTimes)}
            className="text-xs text-violet-700 hover:text-violet-900 underline"
          >
            {hasTimes ? "Remove time" : "Add time"}
          </button>
        </div>
        {hasTimes && (
          <div className="flex items-center gap-2 mt-1">
            <select
              value={startTime}
              onChange={(e) => setStartTime(Number(e.target.value))}
              className="flex-1 min-w-0 text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
            >
              {QHNUM_OPTIONS.map((q) => (
                <option key={`start-${q}`} value={q}>
                  {fmtQhnum(q)}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-400 shrink-0">to</span>
            <select
              value={endTime}
              onChange={(e) => setEndTime(Number(e.target.value))}
              className="flex-1 min-w-0 text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
            >
              {QHNUM_OPTIONS.filter((q) => q > 0).map((q) => (
                <option key={`end-${q}`} value={q}>
                  {fmtQhnum(q)}
                </option>
              ))}
              <option value={24}>Midnight</option>
            </select>
          </div>
        )}
        {timesInvalid && (
          <div className="text-[11px] text-red-500">
            The end time must be after the start time.
          </div>
        )}

        {/* Visibility */}
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
            className="accent-violet-700"
          />
          Visible to team
          <span className="text-xs text-gray-500">
            {visible ? "" : "(manager-only)"}
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 px-3 py-2 bg-white border-t border-violet-100">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={loading || timesInvalid}
          className="px-4 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <CircularProgress size={14} color="inherit" />}
          Confirm
        </button>
      </div>
    </div>
  );
}
